import type { FiatProvider, KYCProvider } from '@funkit/connect-core'

/**
 * The documents the identity step points at.
 *
 * Held together because two screens link them, and a provider change is one
 * edit rather than a hunt through the copy.
 */

/** Fun's own policy, linked from the consent the buyer has to give. */
export const PRIVACY_POLICY_URL = 'https://fun.xyz/privacy'

/**
 * Bumped whenever the consent copy changes, and logged with every consent, so
 * a record can be matched to the words the buyer actually read.
 */
export const CONSENT_COPY_VERSION = '2026-09-11'

/**
 * The terms of the provider that settles the order, keyed by the provider the
 * round names.
 *
 * Only verified URLs belong here. A provider absent from the map is named in
 * the copy without a link and logged (`kyc_provider_terms_url_missing`) — it
 * is never guessed, and never falls back to another provider's terms.
 */
const PROVIDER_TERMS_URLS: Partial<Record<FiatProvider, string>> = {
  BANXA: 'https://banxa.com/terms-of-use/',
}

/** How each provider writes its own name; the enum is upper-case. */
const PROVIDER_NAMES: Partial<Record<FiatProvider, string>> = {
  BANXA: 'Banxa',
  TRANSAK: 'Transak',
}

/** The identity vendors, which settle no orders and so are kept apart. */
const KYC_PROVIDER_NAMES: Partial<Record<KYCProvider, string>> = {
  SUMSUB: 'Sumsub',
}

/** Title-cases an upper-case enum member for a provider with no name on record. */
const titleCase = (value: string) =>
  value.charAt(0) + value.slice(1).toLowerCase()

/** The provider's terms, or `undefined` when none is on record for it. */
export function providerTermsUrl(
  provider: FiatProvider | undefined,
): string | undefined {
  return provider === undefined ? undefined : PROVIDER_TERMS_URLS[provider]
}

/**
 * The provider's name as the copy should print it. `fallback` is what a
 * screen drawn with no round to name one says instead.
 */
export function providerDisplayName(
  provider: FiatProvider | undefined,
  fallback: string,
): string {
  if (provider === undefined) {
    return fallback
  }
  return PROVIDER_NAMES[provider] ?? titleCase(provider)
}

/**
 * The identity vendor's name as the copy should print it. Separate from the
 * settling provider: the two enums name different things and never mix.
 */
export function kycProviderDisplayName(
  provider: KYCProvider | undefined,
  fallback: string,
): string {
  if (provider === undefined) {
    return fallback
  }
  return KYC_PROVIDER_NAMES[provider] ?? titleCase(provider)
}
